import { useEffect, useState, type FormEvent } from 'react';
import { Plus, Trash2, ListOrdered, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { useAccounts, useCreateJournalEntry, useUpdateJournalEntry, type SaveJournalInput } from '@/hooks/useAccounting';
import { sumLines, type JournalEntry, type JournalLine } from '@/types/accounting';
import { formatCurrency } from '@/lib/utils';

type Props = {
  open: boolean;
  onClose: () => void;
  entry?: JournalEntry | null;
};

function today() { return new Date().toISOString().slice(0, 10); }

function newLine(): JournalLine {
  return { id: crypto.randomUUID(), accountId: '', debit: 0, credit: 0, description: '' };
}

const EMPTY: SaveJournalInput = {
  status: 'draft',
  source: 'manual',
  date: today(),
  memo: '',
  reference: '',
  lines: [newLine(), newLine()],
};

export function JournalEntryModal({ open, onClose, entry }: Props) {
  const { data: accounts } = useAccounts({ active: true });
  const create = useCreateJournalEntry();
  const update = useUpdateJournalEntry();
  const editing = !!entry;

  const [form, setForm] = useState<SaveJournalInput>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(entry ? hydrate(entry) : { ...EMPTY, lines: [newLine(), newLine()] });
      setError(null);
    }
  }, [open, entry]);

  const totals = sumLines(form.lines);

  function updateLine(idx: number, patch: Partial<JournalLine>) {
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l, i) => {
        if (i !== idx) return l;
        const merged = { ...l, ...patch };
        // If user enters a debit, clear credit (and vice versa)
        if (patch.debit !== undefined && patch.debit > 0) merged.credit = 0;
        if (patch.credit !== undefined && patch.credit > 0) merged.debit = 0;
        return merged;
      }),
    }));
  }

  function removeLine(idx: number) {
    if (form.lines.length <= 2) return; // minimum 2 lines
    setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  }

  function addLine() {
    setForm((f) => ({ ...f, lines: [...f.lines, newLine()] }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.memo.trim()) { setError('Please enter a memo describing this entry.'); return; }
    if (!totals.balanced) {
      setError(`Entry is not balanced. Debits and credits must be equal (difference: ${formatCurrency(Math.abs(totals.difference))}).`);
      return;
    }
    if (form.lines.some((l) => !l.accountId)) {
      setError('Every line must reference an account.');
      return;
    }

    try {
      if (editing && entry) {
        await update.mutateAsync({ id: entry.id, ...form });
        toast.success(`Updated ${entry.number}`);
      } else {
        const result = await create.mutateAsync(form);
        toast.success(`Created ${result.number}`);
      }
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Save failed.');
    }
  }

  const submitting = create.isPending || update.isPending;
  const accountOpts = [
    { value: '', label: '— Select account —' },
    ...(accounts ?? [])
      .slice()
      .sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }))
      .map((a) => ({ value: a.id, label: `${a.number} · ${a.name}` })),
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker={editing ? 'Edit Journal Entry' : 'New Journal Entry'}
      title={editing ? `Edit ${entry?.number}` : 'Record a journal entry.'}
      description={editing ? undefined : 'Every entry must balance — debits equal credits. Drafts can be edited; posted entries are immutable.'}
      size="lg"
      footer={
        <>
          <div className="flex-1 flex items-center gap-3">
            {totals.balanced ? (
              <Badge tone="success"><Check size={10} /> Balanced</Badge>
            ) : totals.totalDebit > 0 || totals.totalCredit > 0 ? (
              <Badge tone="warning">Out of balance: {formatCurrency(Math.abs(totals.difference))}</Badge>
            ) : (
              <Badge tone="neutral">Empty</Badge>
            )}
          </div>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            type="submit" form="je-form"
            loading={submitting}
            disabled={!totals.balanced}
            leftIcon={<ListOrdered size={13} />}
          >
            {editing ? 'Save Changes' : 'Save as Draft'}
          </Button>
        </>
      }
    >
      <form id="je-form" onSubmit={onSubmit} className="space-y-5 py-2">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Date"
            type="date"
            required
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
          />
          <Input
            label="Reference"
            placeholder="Optional — external doc #"
            value={form.reference ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
          />
        </div>

        <Input
          label="Memo"
          required
          placeholder="e.g. Adjusting entry for Q1 depreciation"
          value={form.memo}
          onChange={(e) => setForm((f) => ({ ...f, memo: e.target.value }))}
        />

        {/* Lines */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="kicker">Lines</div>
            <Button size="sm" variant="secondary" leftIcon={<Plus size={13} />} type="button" onClick={addLine}>
              Add Line
            </Button>
          </div>

          <div className="bg-bg-subtle border border-border rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-bg-panel">
                  <th className="kicker px-3 py-2 font-normal text-left">Account</th>
                  <th className="kicker px-3 py-2 font-normal text-left">Description</th>
                  <th className="kicker px-3 py-2 font-normal text-right w-32">Debit</th>
                  <th className="kicker px-3 py-2 font-normal text-right w-32">Credit</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {form.lines.map((line, idx) => (
                  <tr key={line.id} className="border-b border-border last:border-0">
                    <td className="px-2 py-2 w-64">
                      <select
                        value={line.accountId}
                        onChange={(e) => updateLine(idx, { accountId: e.target.value })}
                        className="w-full h-9 bg-bg-panel border border-border rounded-sm px-2 text-xs text-text focus:border-brass-400 outline-none"
                      >
                        {accountOpts.map((o) => (
                          <option key={o.value} value={o.value} className="bg-bg-panel">{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="text"
                        placeholder="Line description (optional)"
                        value={line.description ?? ''}
                        onChange={(e) => updateLine(idx, { description: e.target.value })}
                        className="w-full h-9 bg-bg-panel border border-border rounded-sm px-2 text-xs text-text placeholder:text-text-dim focus:border-brass-400 outline-none"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number" min="0" step="0.01"
                        placeholder="0.00"
                        value={line.debit || ''}
                        onChange={(e) => updateLine(idx, { debit: Number(e.target.value) || 0 })}
                        className="w-full h-9 bg-bg-panel border border-border rounded-sm px-2 text-xs text-text text-right tabular focus:border-brass-400 outline-none"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number" min="0" step="0.01"
                        placeholder="0.00"
                        value={line.credit || ''}
                        onChange={(e) => updateLine(idx, { credit: Number(e.target.value) || 0 })}
                        className="w-full h-9 bg-bg-panel border border-border rounded-sm px-2 text-xs text-text text-right tabular focus:border-brass-400 outline-none"
                      />
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        disabled={form.lines.length <= 2}
                        className="p-1.5 text-text-dim hover:text-danger hover:bg-danger/10 rounded-sm disabled:opacity-30 disabled:hover:text-text-dim disabled:hover:bg-transparent"
                      >
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-bg-panel border-t border-border-strong">
                  <td colSpan={2} className="px-3 py-3 text-right">
                    <span className="kicker">Totals</span>
                  </td>
                  <td className="px-3 py-3 text-right tabular font-mono text-text">
                    {formatCurrency(totals.totalDebit)}
                  </td>
                  <td className="px-3 py-3 text-right tabular font-mono text-text">
                    {formatCurrency(totals.totalCredit)}
                  </td>
                  <td />
                </tr>
                {!totals.balanced && (totals.totalDebit > 0 || totals.totalCredit > 0) && (
                  <tr className="bg-danger/5 border-t border-danger/30">
                    <td colSpan={5} className="px-3 py-2 text-xs text-danger flex items-center gap-2">
                      <AlertCircle size={12} />
                      Out of balance by {formatCurrency(Math.abs(totals.difference))}.
                      Adjust lines until debits equal credits.
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        </div>

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">{error}</div>
        )}

        <div className="bg-bg-subtle border border-border rounded-sm p-4 text-xs text-text-muted leading-relaxed">
          <div className="kicker text-text mb-2">About Journal Entries</div>
          Draft entries don't affect account balances. They do so only when <span className="text-text">posted</span>.
          Once posted, an entry becomes immutable — use <span className="text-text">Reverse</span> to create a mirror-image correcting entry
          instead of editing or deleting.
        </div>
      </form>
    </Modal>
  );
}

function hydrate(e: JournalEntry): SaveJournalInput {
  return {
    status: e.status, source: e.source, date: e.date?.slice(0, 10),
    memo: e.memo, reference: e.reference ?? '',
    lines: e.lines,
    sourceDocType: e.sourceDocType, sourceDocId: e.sourceDocId, sourceDocNumber: e.sourceDocNumber,
  };
}
